import React, { useEffect, useState } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

/* ================= CONFIG ================= */

const API_BASE =
  process.env.REACT_APP_SELLER_API ||
  "https://gallery.pitics.com";

/* ================= CONSTANTS ================= */

const MIN_CUSTOMER_PHOTOS = 1;
const MAX_CUSTOMER_PHOTOS = 12;

const MIN_PRODUCT_IMAGES = 1;
const MAX_PRODUCT_IMAGES = 12;
const IMAGE_ORIENTATIONS = [
  { value: "landscape", label: "Landscape" },
  { value: "portrait", label: "Portrait" },
  { value: "square", label: "Square" }
];

const MAX_VARIANT_OPTIONS = 5;
const MAX_VARIANT_VALUES = 50;
const MAX_VARIANTS = 250;

const QUICK_OPTION_PRESETS = {
  Size: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
  Color: ["Black", "White", "Red", "Blue", "Green", "Yellow", "Pink", "Purple", "Orange", "Brown", "Grey"],
  Material: ["Cotton", "Polyester", "Leather", "Metal", "Wood"],
  Storage: ["32 GB", "64 GB", "128 GB", "256 GB", "512 GB", "1 TB"],
  Style: ["Regular", "Slim", "Classic", "Modern"]
};

function makeVariantRows(options, existing = []) {
  if (!Array.isArray(options) || !options.length) return [];

  const normalized = options.map((o) => ({
    name: String(o?.name || "").trim(),
    values: [...new Set(
      (Array.isArray(o?.values) ? o.values : [])
        .map((v) => String(v || "").trim())
        .filter(Boolean)
    )].slice(0, MAX_VARIANT_VALUES)
  }));

  if (normalized.some((o) => !o.name || !o.values.length)) return [];

  let combinations = [{}];

  for (const option of normalized) {
    combinations = combinations.flatMap((row) =>
      option.values.map((value) => ({ ...row, [option.name]: value }))
    );
    if (combinations.length > MAX_VARIANTS) return [];
  }

  return combinations.map((combo) => {
    const old = Array.isArray(existing)
      ? existing.find((v) =>
          Object.keys(combo).every(
            (key) =>
              String(v?.options?.[key] ?? "").toLowerCase() ===
              String(combo[key]).toLowerCase()
          )
        )
      : null;

    return {
      variantId: old?.variantId || "",
      options: combo,
      sku: old?.sku || "",
      mrp: old?.mrp ?? "",
      sellingPrice: old?.sellingPrice ?? "",
      stockQty: old?.stockQty ?? "",
      minOrderQty: old?.minOrderQty ?? 1,
      orderStep: old?.orderStep ?? 1,
      active: old?.active !== false
    };
  });
}

/* ================= DEFAULT FORM ================= */

const defaultForm = {
  name: "",
  category: "",
  subcategory: "",
  subsubcategory: "",
  mrp: "",
  sellingPrice: "",
  isBundle: false,
  bundleQuantity: 2,
  quantity: "",
  minOrderQty: "",
  orderStep: "",
  description: "",
  sku: "",
  brand: "",
  hsn: "",
  weight: "",

  /* personalized config only */
  requiresName: false,
  requiresPhoto: false,
  minPhotos: MIN_CUSTOMER_PHOTOS,
  maxPhotos: MIN_CUSTOMER_PHOTOS,
  youtubeShortUrl: "",
  productOptions: [],
  variants: [],
  shareGiftEnabled: false,
  returnAllowed: false,
  replacementAllowed: false,
  returnWindowDays: 7,
  shareGiftProductName: "",
  shareGiftProductPrice: "",
  shareGiftProductQty: "",
};

/* ================= COMPONENT ================= */

const ListingItem = () => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [images, setImages] = useState([]);
  const [productPreviewImage, setProductPreviewImage] = useState(null);
  const [shareGiftImage, setShareGiftImage] = useState(null);
  const [approvedRootCats, setApprovedRootCats] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [youtubeShortsEnabled, setYoutubeShortsEnabled] = useState(false);
  const [youtubeShortsRequested, setYoutubeShortsRequested] = useState(false);
  const [sellerLoading, setSellerLoading] = useState(true);
  const [defaultOrientation, setDefaultOrientation] = useState("landscape");

  /* ================= FETCH CATEGORIES ================= */

  useEffect(() => {
    const load = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;
        const headers = { "X-Firebase-Authorization": token };
        const [allowedRes, meRes] = await Promise.all([
          fetch(`${API_BASE}/api/seller/allowed-categories`, { headers }),
          fetch(`${API_BASE}/api/seller/me`, { headers })
        ]);
        const [allowedData, meData] = await Promise.all([allowedRes.json(), meRes.json()]);
        if (allowedData.success) {
          setCategories(allowedData.categories || []);
          setRootCategories(allowedData.rootCategories || []);
        }
        if (meData.success) {
          setApprovedRootCats(Array.isArray(meData.approvedRootCats) ? meData.approvedRootCats : []);

          // /api/seller/me currently returns these flags at the top level.
          // Also accept a nested seller object so the UI remains compatible
          // if the API response is wrapped by a proxy/response adapter.
          const sellerAccess = meData.seller && typeof meData.seller === "object"
            ? meData.seller
            : {};
          setYoutubeShortsRequested(
            Boolean(meData.youtubeShortsRequested ?? sellerAccess.youtubeShortsRequested)
          );
          setYoutubeShortsEnabled(
            Boolean(meData.youtubeShortsEnabled ?? sellerAccess.youtubeShortsEnabled)
          );
        } else {
          setYoutubeShortsRequested(false);
          setYoutubeShortsEnabled(false);
          console.warn("Seller access endpoint did not return success:", meData.error || meData.message);
        }
      } catch (err) {
        console.error("❌ Seller/category access load error:", err);
        alert(err.message || "Failed to load seller access");
      } finally {
        setSellerLoading(false);
      }
    };
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) load();
      else setSellerLoading(false);
    });
    return () => unsub();
  }, []);

  /* ================= CLEANUP ================= */

  useEffect(() => {
    return () => {
      images.forEach((item) => {
        if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, [images]);

  useEffect(() => {
    return () => {
      if (productPreviewImage?.previewUrl) URL.revokeObjectURL(productPreviewImage.previewUrl);
    };
  }, [productPreviewImage]);

  /* ================= HELPERS ================= */

  const categoryRoot = (categoryId) => {
    const id = String(categoryId || "").toLowerCase();
    return rootCategories.find((root) =>
      (root.categories || []).some(
        (category) => String(category.category || "").toLowerCase() === id
      )
    ) || null;
  };

  const selectedRoot = categoryRoot(form.category);
  const isPersonalized = selectedRoot?.id === "personalized";

  /* The backend already filters these categories using the seller's
     approvedRootCats. Keep a defensive local filter as well. */
  const allowedCategories = categories.filter((c) =>
    rootCategories.some((root) =>
      (root.categories || []).some(
        (category) => String(category.category || "").toLowerCase() === String(c.category || "").toLowerCase()
      )
    )
  );

  const selectedCategory =
    rootCategories
      .flatMap((root) => root.categories || [])
      .find((c) => String(c.category || "").trim().toLowerCase() === String(form.category || "").trim().toLowerCase()) ||
    allowedCategories.find(
      (c) => String(c.category || "").trim().toLowerCase() === String(form.category || "").trim().toLowerCase()
    ) || null;

  const isAllProducts = ["all products", "all_products", "allproducts"].includes(
    String(form.category || "").trim().toLowerCase()
  );
  const childMap = selectedCategory?.subsubcategoryMap || {};
  const normalizedSubcategory = String(form.subcategory || "").trim().toLowerCase();
  const selectedSubcategoryChildren = Object.entries(childMap).find(
    ([key]) => String(key).trim().toLowerCase() === normalizedSubcategory
  )?.[1] || [];

  // Product code preview mirrors the backend format. The final unique
  // 8-digit series is reserved by the backend only when the product is saved.
  const productCodeSegment = (value, fallback = "G") => {
    // Keep the preview identical to the backend: one character per level.
    const firstCharacter = String(value || "")
      .normalize("NFKD")
      .replace(/[^a-zA-Z0-9]/g, "")
      .charAt(0);
    return (firstCharacter || fallback).toUpperCase();
  };

  const productCodeRootPrefix =
    isAllProducts ? "A" :
    selectedRoot?.id === "personalized" ? "P" :
    selectedRoot?.id === "semi_personalized" ? "S" : "";

  const productCodePreview = productCodeRootPrefix && form.subcategory
    ? [
        productCodeRootPrefix,
        productCodeSegment(form.subcategory),
        ...(isAllProducts && form.subsubcategory
          ? [productCodeSegment(form.subsubcategory)]
          : []),
        "########"
      ].join("")
    : "";

  /* ================= VARIANT HELPERS ================= */

  const addOption = (presetName = "") => {
    setForm((prev) => {
      if (prev.productOptions.length >= MAX_VARIANT_OPTIONS) {
        alert(`A maximum of ${MAX_VARIANT_OPTIONS} product options is allowed.`);
        return prev;
      }

      const duplicate = prev.productOptions.some(
        (o) =>
          String(o?.name || "").trim().toLowerCase() ===
          String(presetName || "").trim().toLowerCase()
      );

      if (presetName && duplicate) return prev;

      return {
        ...prev,
        productOptions: [
          ...prev.productOptions,
          {
            name: presetName,
            values: [...(QUICK_OPTION_PRESETS[presetName] || [""])]
          }
        ],
        variants: []
      };
    });
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      productOptions: prev.productOptions.filter((_, i) => i !== index),
      variants: []
    }));
  };

  const updateOption = (index, name) => {
    setForm((prev) => {
      const options = [...prev.productOptions];
      const duplicate = options.some(
        (o, i) =>
          i !== index &&
          String(o?.name || "").trim().toLowerCase() ===
          String(name || "").trim().toLowerCase()
      );

      if (duplicate) {
        alert(`Option "${name.trim()}" is already added.`);
        return prev;
      }

      options[index] = { ...options[index], name };

      return { ...prev, productOptions: options, variants: [] };
    });
  };

  const addOptionValue = (optionIndex) => {
    setForm((prev) => {
      const options = [...prev.productOptions];
      const values = [...(options[optionIndex]?.values || [])];

      if (values.length >= MAX_VARIANT_VALUES) {
        alert(`An option can have a maximum of ${MAX_VARIANT_VALUES} values.`);
        return prev;
      }

      values.push("");
      options[optionIndex] = { ...options[optionIndex], values };

      return { ...prev, productOptions: options, variants: [] };
    });
  };

  const updateOptionValue = (optionIndex, valueIndex, value) => {
    setForm((prev) => {
      const options = [...prev.productOptions];
      const values = [...(options[optionIndex]?.values || [])];
      values[valueIndex] = value;
      options[optionIndex] = { ...options[optionIndex], values };

      return { ...prev, productOptions: options, variants: [] };
    });
  };

  const removeOptionValue = (optionIndex, valueIndex) => {
    setForm((prev) => {
      const options = [...prev.productOptions];
      options[optionIndex] = {
        ...options[optionIndex],
        values: options[optionIndex].values.filter((_, i) => i !== valueIndex)
      };

      return { ...prev, productOptions: options, variants: [] };
    });
  };

  const generateVariants = () => {
    const rows = makeVariantRows(form.productOptions, form.variants);

    if (!rows.length) {
      alert(
        `Enter a unique option name and at least one value for every option. Maximum ${MAX_VARIANTS} combinations.`
      );
      return;
    }

    setForm((prev) => ({ ...prev, variants: rows }));
  };

  const updateVariantField = (index, field, value) => {
    setForm((prev) => {
      const variants = [...prev.variants];
      variants[index] = { ...variants[index], [field]: value };
      return { ...prev, variants };
    });
  };

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    const { name, value, type, checked } =
      e.target;

    const finalValue =
      type === "checkbox" ? checked : value;

    if (name === "category") {
      const nextCategory = value;

      const nextIsPersonalized =
  String(nextCategory || "")
    .toLowerCase()
    .includes("printed")
  ||
  String(nextCategory || "")
    .toLowerCase() ===
    "personalized";

      setForm((prev) => ({
        ...prev,
        category: nextCategory,
        subcategory: "",
        subsubcategory: "",

        requiresName: nextIsPersonalized
          ? prev.requiresName
          : false,

        requiresPhoto: nextIsPersonalized
          ? prev.requiresPhoto
          : false,

        minPhotos: MIN_CUSTOMER_PHOTOS,

        maxPhotos: nextIsPersonalized
          ? prev.maxPhotos
          : MIN_CUSTOMER_PHOTOS,
      }));

      return;
    }

    if (name === "subcategory") {
      setForm((prev) => ({ ...prev, subcategory: value, subsubcategory: "" }));
      return;
    }

    if (name === "requiresPhoto") {
      setForm((prev) => ({
        ...prev,
        requiresPhoto: checked,
        minPhotos: MIN_CUSTOMER_PHOTOS,
        maxPhotos: checked
          ? prev.maxPhotos ||
            MIN_CUSTOMER_PHOTOS
          : MIN_CUSTOMER_PHOTOS,
      }));

      return;
    }

    if (name === "maxPhotos") {
      setForm((prev) => ({
        ...prev,
        maxPhotos: Number(value),
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(
      e.target.files || []
    );

    setImages((prev) => {
      const remaining = MAX_PRODUCT_IMAGES - prev.length;
      const accepted = files.slice(0, Math.max(0, remaining)).map((file) => ({ file, orientation: defaultOrientation, previewUrl: URL.createObjectURL(file) }));
      if (files.length > accepted.length) alert(`Maximum ${MAX_PRODUCT_IMAGES} product images allowed`);
      return [...prev, ...accepted];
    });

    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  /* ================= VALIDATION ================= */

  const validateRules = () => {
    const stock = Number(form.quantity);
    const min = Number(form.minOrderQty);
    const step = Number(form.orderStep);
    const mrp = Number(form.mrp);
    const sell = Number(form.sellingPrice);
    const bundleQuantity = Number(form.bundleQuantity);

    if (!form.category || !form.subcategory) {
      alert(
        "Please select category and subcategory"
      );
      return false;
    }

    if (isAllProducts && !form.subsubcategory) {
      alert(
        "Please select Subcategory 2 for All Products"
      );
      return false;
    }

    if (!Number.isFinite(mrp) || mrp <= 0) {
      alert("MRP must be greater than 0");
      return false;
    }

    if (!Number.isFinite(sell) || sell <= 0) {
      alert("Selling price must be greater than 0");
      return false;
    }

    if (sell > mrp) {
      alert(
        "Selling price cannot exceed MRP"
      );
      return false;
    }

    if (form.isBundle) {
      if (!Number.isInteger(bundleQuantity) || bundleQuantity < 2) {
        alert("Bundle quantity must be an integer of at least 2");
        return false;
      }
    }

    if (!Number.isFinite(stock) || stock < 0) {
      alert("Stock quantity must be 0 or greater");
      return false;
    }

    if (!Number.isFinite(min) || min < 1) {
      alert("Minimum Order Qty must be at least 1");
      return false;
    }

    if (!Number.isFinite(step) || step < 1) {
      alert("Order Step must be at least 1");
      return false;
    }

    if (stock < min) {
      alert(
        `Stock must be at least ${min}`
      );
      return false;
    }

    if ((stock - min) % step !== 0) {
      alert(
        `Stock must increase in steps of ${step}`
      );
      return false;
    }

    if (images.length < 1) {
      alert("Please upload at least 1 product image");
      return false;
    }

    if (form.productOptions.length) {
      const rows = makeVariantRows(form.productOptions, form.variants);

      if (!rows.length || rows.length !== form.variants.length) {
        alert("Generate the complete set of valid product variants before listing.");
        return false;
      }

      const seenNames = new Set();

      for (const option of form.productOptions) {
        const name = String(option?.name || "").trim();
        const key = name.toLowerCase();

        if (!name) {
          alert("Every product option needs a name.");
          return false;
        }

        if (seenNames.has(key)) {
          alert(`Duplicate product option: ${name}`);
          return false;
        }
        seenNames.add(key);

        const values = [...new Set(
          (option.values || [])
            .map((v) => String(v || "").trim())
            .filter(Boolean)
        )];

        if (!values.length) {
          alert(`Add at least one value for ${name}.`);
          return false;
        }
      }

      for (let i = 0; i < form.variants.length; i++) {
        const v = form.variants[i];
        const mrp = Number(v.mrp);
        const selling = Number(v.sellingPrice);
        const stock = Math.floor(Number(v.stockQty));
        const min = Math.max(1, Math.floor(Number(v.minOrderQty) || 1));
        const step = Math.max(1, Math.floor(Number(v.orderStep) || 1));

        if (!String(v.sku || "").trim()) {
          alert(`Variant ${i + 1}: SKU is required.`);
          return false;
        }
        if (!Number.isFinite(mrp) || mrp <= 0) {
          alert(`Variant ${i + 1}: MRP must be greater than 0.`);
          return false;
        }
        if (!Number.isFinite(selling) || selling <= 0 || selling > mrp) {
          alert(`Variant ${i + 1}: selling price must be valid and cannot exceed MRP.`);
          return false;
        }
        if (!Number.isFinite(stock) || stock < min) {
          alert(`Variant ${i + 1}: stock must be at least minimum order quantity.`);
          return false;
        }
        if ((stock - min) % step !== 0) {
          alert(`Variant ${i + 1}: stock is invalid for the selected order step.`);
          return false;
        }
      }
    }

    // Every new listing must explicitly offer at least one customer remedy.
    if (!form.returnAllowed && !form.replacementAllowed) {
      alert("Enable at least one option: Returns or Replacement. This is mandatory for every product.");
      return false;
    }
    const policyWindowDays = Number(form.returnWindowDays);
    if (!Number.isInteger(policyWindowDays) || policyWindowDays < 1 || policyWindowDays > 90) {
      alert("Return / replacement window must be between 1 and 90 days.");
      return false;
    }

    if (isPersonalized) {
      if (
        !form.requiresName &&
        !form.requiresPhoto
      ) {
        alert(
          "Choose at least one personalized option"
        );
        return false;
      }

      if (form.requiresPhoto) {
        const max =
          Number(form.maxPhotos);

        if (
          max <
          MIN_CUSTOMER_PHOTOS
        ) {
          alert(
            `Minimum customer photos must be ${MIN_CUSTOMER_PHOTOS}`
          );
          return false;
        }

        if (
          max >
          MAX_CUSTOMER_PHOTOS
        ) {
          alert(
            `Maximum customer photos can be ${MAX_CUSTOMER_PHOTOS}`
          );
          return false;
        }
      }
    }

    // Share & Earn is configured on this listing/product. It does not
    // reference another seller catalogue product.
    if (form.shareGiftEnabled) {
      const giftName = String(form.shareGiftProductName || "").trim();
      const giftPrice = Number(form.shareGiftProductPrice);
      const giftQty = Number(form.shareGiftProductQty);
      if (!giftName || giftName.length > 150) {
        alert("Enter a valid free gift product name (maximum 150 characters).");
        return false;
      }
      if (!Number.isFinite(giftPrice) || giftPrice <= 0) {
        alert("Enter a valid free gift price greater than zero.");
        return false;
      }
      if (!Number.isInteger(giftQty) || giftQty < 1) {
        alert("Free gift quantity must be a whole number of at least 1.");
        return false;
      }
      if (!shareGiftImage) {
        alert("Upload a preview image for the free gift.");
        return false;
      }
    }

    if (youtubeShortsEnabled && form.youtubeShortUrl.trim()) {
      const re = /^https:\/\/www\.youtube\.com\/shorts\/[A-Za-z0-9_-]+(?:\?.*)?$/i;
      if (!re.test(form.youtubeShortUrl.trim())) {
        alert("Only a valid https://www.youtube.com/shorts/VIDEO_ID URL is accepted");
        return false;
      }
    }
    if (!youtubeShortsEnabled && form.youtubeShortUrl.trim()) {
      alert("YouTube video demo access is not enabled for this seller");
      return false;
    }

    return true;
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert("Please login as seller");
      return;
    }

    if (!validateRules()) return;

    try {
      setLoading(true);

      const token =
        await auth.currentUser.getIdToken();

      const formData = new FormData();

      formData.append(
        "payload",
        JSON.stringify({
          name: form.name.trim(),

          category:
            form.category.toLowerCase(),

          subcategory:
            form.subcategory.toLowerCase(),

          subsubcategory:
            form.subsubcategory.toLowerCase(),

          description:
            form.description.trim(),

          sku: form.sku.trim(),
          brand: form.brand.trim(),
          hsn: form.hsn.trim(),
          weight: form.weight.trim(),

          mrp: Number(form.mrp),

          sellingPrice: Number(
            form.sellingPrice
          ),

          /* seller-declared customer service policy */
          returnAllowed: Boolean(form.returnAllowed),
          replacementAllowed: Boolean(form.replacementAllowed),
          returnWindowDays: Math.max(0, Math.floor(Number(form.returnWindowDays) || 0)),

          /* bundle config */
          isBundle: Boolean(form.isBundle),
          freeProductOffer: false,
          shareGiftEnabled: Boolean(form.shareGiftEnabled),
          shareGiftProductName: form.shareGiftEnabled ? form.shareGiftProductName.trim() : "",
          shareGiftProductPrice: form.shareGiftEnabled ? Number(form.shareGiftProductPrice) : 0,
          shareGiftProductQty: form.shareGiftEnabled ? Math.floor(Number(form.shareGiftProductQty)) : 0,
          bundleQuantity: form.isBundle
            ? Number(form.bundleQuantity)
            : 1,

          stockQty: Number(
            form.quantity
          ),

          minOrderQty: Number(
            form.minOrderQty
          ),

          orderStep: Number(
            form.orderStep
          ),

          /* personalized config only */
          requiresName:
            isPersonalized &&
            form.requiresName,

          requiresPhoto:
            isPersonalized &&
            form.requiresPhoto,

          minPhotos:
            isPersonalized &&
            form.requiresPhoto
              ? MIN_CUSTOMER_PHOTOS
              : 0,

          maxPhotos:
            isPersonalized &&
            form.requiresPhoto
              ? Number(form.maxPhotos)
              : 0,

          youtubeShortUrl:
            youtubeShortsEnabled
              ? form.youtubeShortUrl.trim()
              : "",

          imageOrientations: JSON.stringify(
            images.map((item) => item.orientation)
          ),

          hasVariants:
            form.productOptions.length > 0,

          productOptions:
            form.productOptions,

          variants:
            form.productOptions.length
              ? form.variants.map((variant) => ({
                  ...variant,
                  mrp: Number(variant.mrp),
                  sellingPrice: Number(variant.sellingPrice),
                  stockQty: Math.floor(Number(variant.stockQty)),
                  minOrderQty: Math.max(1, Math.floor(Number(variant.minOrderQty) || 1)),
                  orderStep: Math.max(1, Math.floor(Number(variant.orderStep) || 1))
                }))
              : []
        })
      );

      formData.append("productPreviewImage", productPreviewImage.file);
      if (form.shareGiftEnabled && shareGiftImage) formData.append("shareGiftImage", shareGiftImage);

      if (images[0]?.file) {
        formData.append("productImage", images[0].file);
      }

      images.slice(1).forEach((item) => {
        formData.append("extraFile", item.file);
      });

      const res = await fetch(
        `${API_BASE}/api/seller/create-product`,
        {
          method: "POST",
          headers: {
            "X-Firebase-Authorization":
              token,
          },
          body: formData,
        }
      );

      const data = await res.json();

      if (!data.success) {
        throw new Error(
          data.error || "Failed"
        );
      }

      alert(
        `✅ Product listed successfully${data.productCode ? `\\nProduct Code: ${data.productCode}` : ""}`
      );

      setForm(defaultForm);
      setImages([]);
    } catch (err) {
      console.error(err);
      alert(
        err.message ||
          "❌ Failed to list product"
      );
    } finally {
      setLoading(false);
    }
  };

  if (sellerLoading) return <div style={styles.card}>Loading seller access...</div>;

  /* ================= UI ================= */

  return (
    <div style={styles.card}>
      <h2 style={styles.title}>
        Add New Product
      </h2>

      <form
        onSubmit={handleSubmit}
        style={styles.grid}
      >
        {/* PRODUCT NAME */}
        <input
          style={styles.input}
          name="name"
          placeholder="Product Name"
          value={form.name}
          onChange={handleChange}
          required
        />

        {/* CATEGORY */}
        <select
          style={styles.input}
          name="category"
          value={form.category}
          onChange={handleChange}
          required
        >
          <option value="">
            Select Category
          </option>

          {rootCategories.map((root) => (
            <optgroup key={root.id} label={root.name}>
              {(root.categories || []).map((c) => (
                <option key={c.category} value={c.category}>
                  {c.category}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {/* SUBCATEGORY */}
        <select
          style={styles.input}
          name="subcategory"
          value={form.subcategory}
          onChange={handleChange}
          required
        >
          <option value="">
            Select Subcategory
          </option>

          {selectedCategory?.subcategories?.map(
            (sub) => (
              <option
                key={sub}
                value={sub}
              >
                {sub}
              </option>
            )
          )}
        </select>

        {/* SUBCATEGORY 2 — required only for All Products */}
        {isAllProducts && (
          <div style={{ display: "grid", gap: 6 }}>
            <select
              style={styles.input}
              name="subsubcategory"
              value={form.subsubcategory}
              onChange={handleChange}
              required
              disabled={!form.subcategory || selectedSubcategoryChildren.length === 0}
            >
              <option value="">
                {!form.subcategory
                  ? "Select Subcategory 2 (after Subcategory)"
                  : selectedSubcategoryChildren.length === 0
                    ? "No approved Subcategory 2"
                    : "Select Subcategory 2"}
              </option>
              {selectedSubcategoryChildren.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
            {form.subcategory && selectedSubcategoryChildren.length === 0 && (
              <small style={{ color: "#b45309", fontWeight: 700 }}>
                No approved Subcategory 2 exists for this selection. Submit a Subcategory 2 suggestion to Seller Admin for approval.
              </small>
            )}
          </div>
        )}

        {/* PRODUCT OPTIONS & VARIANTS */}
        <div style={styles.variantBox}>
          <div style={styles.variantHeader}>
            <div>
              <div style={styles.variantTitle}>Product Options & Variants</div>
              <div style={styles.variantHint}>
                Add Size, Color, Material, Storage, Style or a custom option.
                Each combination can have its own SKU, price and stock.
              </div>
            </div>

            <div style={styles.quickOptions}>
              {Object.keys(QUICK_OPTION_PRESETS).map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => addOption(name)}
                  disabled={
                    form.productOptions.length >= MAX_VARIANT_OPTIONS ||
                    form.productOptions.some(
                      (o) =>
                        String(o?.name || "").trim().toLowerCase() ===
                        name.toLowerCase()
                    )
                  }
                  style={styles.quickOptionBtn}
                >
                  ＋ {name}
                </button>
              ))}

              <button
                type="button"
                onClick={() => addOption("")}
                disabled={form.productOptions.length >= MAX_VARIANT_OPTIONS}
                style={styles.customOptionBtn}
              >
                ＋ Custom
              </button>
            </div>
          </div>

          {form.productOptions.map((option, optionIndex) => (
            <div key={`option-${optionIndex}`} style={styles.optionEditor}>
              <div style={styles.optionEditorTop}>
                <input
                  value={option.name}
                  onChange={(e) => updateOption(optionIndex, e.target.value)}
                  placeholder="Option name e.g. Color"
                  style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => removeOption(optionIndex)}
                  style={styles.removeOptionBtn}
                >
                  Remove
                </button>
              </div>

              {option.values.map((value, valueIndex) => (
                <div key={`value-${optionIndex}-${valueIndex}`} style={styles.valueRow}>
                  <input
                    value={value}
                    onChange={(e) =>
                      updateOptionValue(optionIndex, valueIndex, e.target.value)
                    }
                    placeholder={`Value ${valueIndex + 1}`}
                    style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeOptionValue(optionIndex, valueIndex)}
                    style={styles.removeValueBtn}
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => addOptionValue(optionIndex)}
                style={styles.addValueBtn}
              >
                ＋ Add Value
              </button>
            </div>
          ))}

          {form.productOptions.length > 0 && (
            <button
              type="button"
              onClick={generateVariants}
              style={styles.generateBtn}
            >
              Generate Variants
            </button>
          )}

          {form.variants.length > 0 && (
            <div style={styles.variantTableWrap}>
              <table style={styles.variantTable}>
                <thead>
                  <tr>
                    {form.productOptions.map((o) => (
                      <th key={o.name || "option"}>{o.name || "Option"}</th>
                    ))}
                    {["SKU", "MRP", "Price", "Stock", "Min", "Step"].map((label) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.variants.map((variant, index) => (
                    <tr key={variant.variantId || index}>
                      {form.productOptions.map((option) => (
                        <td key={option.name || "option"}>
                          {variant.options?.[option.name] || "-"}
                        </td>
                      ))}

                      {[
                        ["sku", "text"],
                        ["mrp", "number"],
                        ["sellingPrice", "number"],
                        ["stockQty", "number"],
                        ["minOrderQty", "number"],
                        ["orderStep", "number"]
                      ].map(([field, type]) => (
                        <td key={field}>
                          <input
                            type={type}
                            min={
                              field === "minOrderQty" || field === "orderStep"
                                ? 1
                                : 0
                            }
                            value={variant[field] ?? ""}
                            onChange={(e) =>
                              updateVariantField(index, field, e.target.value)
                            }
                            style={styles.variantInput}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <small style={styles.variantLimit}>
            Maximum {MAX_VARIANT_OPTIONS} options · {MAX_VARIANT_VALUES} values per
            option · {MAX_VARIANTS} total combinations.
          </small>
        </div>

        {/* PERSONALIZED CONFIG */}
        {isPersonalized && (
          <>
            <div style={styles.optionBox}>
              <label style={styles.checkRow}>
                <input
                  type="checkbox"
                  name="requiresName"
                  checked={
                    form.requiresName
                  }
                  onChange={
                    handleChange
                  }
                />
                Ask Customer Name
              </label>

              <label style={styles.checkRow}>
                <input
                  type="checkbox"
                  name="requiresPhoto"
                  checked={
                    form.requiresPhoto
                  }
                  onChange={
                    handleChange
                  }
                />
                Ask Customer Photo
              </label>
            </div>

            {form.requiresPhoto && (
              <select
                style={styles.input}
                name="maxPhotos"
                value={
                  form.maxPhotos
                }
                onChange={
                  handleChange
                }
              >
                {Array.from(
                  {
                    length:
                      MAX_CUSTOMER_PHOTOS,
                  },
                  (_, i) => {
                    const count =
                      i + 1;

                    return (
                      <option
                        key={count}
                        value={count}
                      >
                        Allow {count}{" "}
                        Customer
                        Photo
                        {count > 1
                          ? "s"
                          : ""}
                      </option>
                    );
                  }
                )}
              </select>
            )}
          </>
        )}

        {/* BUNDLE CONFIG */}
        <div style={styles.bundleBox}>
          <label style={styles.checkRow}>
            <input
              type="checkbox"
              name="isBundle"
              checked={form.isBundle}
              onChange={handleChange}
            />
            📦 Sell this product as a bundle
          </label>

          {form.isBundle && (
            <div style={styles.bundleDetails}>
              <div>
                <label style={styles.fieldLabel}>
                  Units in One Bundle
                </label>
                <input
                  style={styles.input}
                  type="number"
                  name="bundleQuantity"
                  min="2"
                  step="1"
                  value={form.bundleQuantity}
                  onChange={handleChange}
                  required
                />
              </div>

              <div style={styles.bundleInfo}>
                <strong>Bundle pricing</strong>
                <span>
                  Selling Price = price for <b>1 complete bundle</b>
                </span>
                <span>
                  Unit price is calculated automatically by the backend.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* PRICING */}
        <input
          style={styles.input}
          type="number"
          name="mrp"
          placeholder="MRP (₹)"
          value={form.mrp}
          onChange={handleChange}
          required
        />

        <input
          style={styles.input}
          type="number"
          name="sellingPrice"
          placeholder={
            form.isBundle
              ? "Bundle Price (₹)"
              : "Selling Price (₹)"
          }
          value={
            form.sellingPrice
          }
          onChange={handleChange}
          required
        />

        {/* STOCK */}
        <input
          style={styles.input}
          type="number"
          name="quantity"
          placeholder={
            form.isBundle
              ? "Available Bundles"
              : "Stock Quantity"
          }
          value={form.quantity}
          onChange={handleChange}
          required
        />

        <input
          style={styles.input}
          type="number"
          name="minOrderQty"
          placeholder="Minimum Order Qty"
          value={
            form.minOrderQty
          }
          onChange={handleChange}
          required
        />

        <input
          style={styles.input}
          type="number"
          name="orderStep"
          placeholder="Order Step"
          value={
            form.orderStep
          }
          onChange={handleChange}
          required
        />

        {/* DESCRIPTION */}
        <textarea
          style={styles.textarea}
          name="description"
          placeholder="Product Description"
          value={
            form.description
          }
          onChange={handleChange}
        />

        {/* AUTO PRODUCT CODE PREVIEW */}
        <div style={{ marginTop: 4, marginBottom: 8 }}>
          <label style={{ display: "block", fontWeight: 800, marginBottom: 6 }}>
            Automatically Generated Product Code
          </label>
          <input
            style={{ ...styles.input, background: "#f8fafc", color: "#334155" }}
            value={productCodePreview}
            placeholder="Select category and subcategory to preview code"
            readOnly
            aria-readonly="true"
          />
          <small style={{ display: "block", color: "#64748b", marginTop: -6 }}>
            The backend assigns the final unique 8-digit series when you save the listing.
            {isAllProducts ? " Subcategory 2 is included for All Products." : ""}
          </small>
        </div>

        {/* OPTIONAL SELLER SKU OVERRIDE */}
        <input
          style={styles.input}
          name="sku"
          placeholder="Optional: enter your own Seller SKU (leave blank for auto code)"
          value={form.sku}
          onChange={handleChange}
        />

        <input
          style={styles.input}
          name="brand"
          placeholder="Brand"
          value={form.brand}
          onChange={handleChange}
        />

        <input
          style={styles.input}
          name="hsn"
          placeholder="HSN / Tax Code"
          value={form.hsn}
          onChange={handleChange}
        />

        <input
          style={styles.input}
          name="weight"
          placeholder="Weight / Size"
          value={form.weight}
          onChange={handleChange}
        />

        {/* CUSTOMER RETURN / REPLACEMENT POLICY */}
        <section style={{ gridColumn: "1 / -1", padding: 16, borderRadius: 16, border: "1px solid #a7f3d0", background: "linear-gradient(120deg,#f0fdf4,#ecfeff)" }}>
          <strong style={{ display: "block", color: "#065f46", marginBottom: 10 }}>↩️ Customer Return &amp; Replacement Policy</strong>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#14532d" }}>
              <input type="checkbox" checked={Boolean(form.returnAllowed)} onChange={e => setForm(prev => ({ ...prev, returnAllowed: e.target.checked }))} />
              Returns allowed
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#14532d" }}>
              <input type="checkbox" checked={Boolean(form.replacementAllowed)} onChange={e => setForm(prev => ({ ...prev, replacementAllowed: e.target.checked }))} />
              Replacement allowed
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#14532d" }}>
              Return / replacement window
              <input type="number" min="0" max="90" value={form.returnWindowDays} onChange={e => setForm(prev => ({ ...prev, returnWindowDays: e.target.value }))} style={{ ...styles.input, width: 90, margin: 0 }} />
              days
            </label>
          </div>
          <small style={{ display: "block", marginTop: 8, color: "#047857" }}>Choose the policy customers will see. Final eligibility and enforcement must be applied by the PITICS order and returns backend.</small>
        </section>

        <div
          style={{
            gridColumn: "1 / -1",
            padding: 16,
            borderRadius: 12,
            background: youtubeShortsEnabled ? "#fff7ed" : "#f8fafc",
            border: `1px solid ${youtubeShortsEnabled ? "#fed7aa" : "#cbd5e1"}`
          }}
        >
          <label style={{ display: "block", fontWeight: 800, marginBottom: 8 }}>
            🎬 YouTube Short Demo
            {youtubeShortsEnabled && (
              <span style={{ marginLeft: 8, color: "#15803d", fontSize: 12 }}>Access enabled</span>
            )}
          </label>
          <input
            style={{
              ...styles.input,
              width: "100%",
              boxSizing: "border-box",
              background: youtubeShortsEnabled ? "#fff" : "#f1f5f9",
              cursor: youtubeShortsEnabled ? "text" : "not-allowed"
            }}
            name="youtubeShortUrl"
            placeholder={
              youtubeShortsEnabled
                ? "https://www.youtube.com/shorts/VIDEO_ID"
                : "YouTube Shorts access is not enabled for this seller"
            }
            value={form.youtubeShortUrl}
            onChange={handleChange}
            disabled={!youtubeShortsEnabled}
            aria-describedby="youtube-shorts-access-help"
          />
          <small id="youtube-shorts-access-help" style={{ display: "block", marginTop: 7, color: youtubeShortsEnabled ? "#9a3412" : "#475569" }}>
            {youtubeShortsEnabled
              ? "Only YouTube Shorts URLs are accepted."
              : youtubeShortsRequested
                ? "Your request has been submitted. The YouTube Shorts URL field will become editable after Seller Admin enables access."
                : "YouTube Shorts demo access has not been enabled for this seller. Contact Seller Admin if you want this feature enabled."}
          </small>
        </div>

        {/* FREE PROMOTIONAL PRODUCT OFFER */}
        <div style={{ gridColumn: "1 / -1", padding: 16, border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: 12 }}>
          <strong style={{ display: "block", color: "#1e3a8a", marginBottom: 6 }}>🎁 PITICS Share &amp; Earn — Free Gift</strong>
          <p style={{ margin: "0 0 12px", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
            Enable this product for Share &amp; Earn and enter the gift you will provide. The gift is unlocked after 3 different registered PITICS members view the shared link; the customer then has 48 hours to purchase this product. Campaign availability and sharing limits remain controlled by PITICS Admin.
          </p>
          <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, marginBottom: form.shareGiftEnabled ? 12 : 0 }}>
            <input type="checkbox" checked={Boolean(form.shareGiftEnabled)} onChange={(e) => setForm((prev) => ({ ...prev, shareGiftEnabled: e.target.checked }))} />
            Participate in Share &amp; Earn and provide this free gift
          </label>
          {form.shareGiftEnabled && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10, marginTop: 12 }}>
              <label style={{ gridColumn: "1 / -1", fontWeight: 700, fontSize: 13 }}>
                Free gift product name *
                <input required value={form.shareGiftProductName || ""} onChange={(e) => setForm(prev => ({ ...prev, shareGiftProductName: e.target.value }))} placeholder="Enter free gift name" style={{ ...styles.input, width: "100%", boxSizing: "border-box", marginTop: 5 }} />
              </label>
              <label style={{ fontWeight: 700, fontSize: 13 }}>
                Gift value / price (₹) *
                <input required type="number" min="0.01" step="0.01" value={form.shareGiftProductPrice || ""} onChange={(e) => setForm(prev => ({ ...prev, shareGiftProductPrice: e.target.value }))} placeholder="Gift price" style={{ ...styles.input, width: "100%", boxSizing: "border-box", marginTop: 5 }} />
              </label>
              <label style={{ fontWeight: 700, fontSize: 13 }}>
                Available gift quantity *
                <input required type="number" min="1" step="1" value={form.shareGiftProductQty || ""} onChange={(e) => setForm(prev => ({ ...prev, shareGiftProductQty: e.target.value }))} placeholder="Gift stock quantity" style={{ ...styles.input, width: "100%", boxSizing: "border-box", marginTop: 5 }} />
              </label>
              <label style={{ gridColumn: "1 / -1", fontWeight: 700, fontSize: 13 }}>
                Gift product preview image *
                <input required type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setShareGiftImage(e.target.files?.[0] || null)} style={{ ...styles.input, width: "100%", boxSizing: "border-box", marginTop: 5 }} />
                <small style={{ display: "block", color: "#475569", fontWeight: 400, marginTop: 4 }}>{shareGiftImage ? `Selected: ${shareGiftImage.name}` : "Upload a clear JPG, PNG or WEBP image of the free gift."}</small>
                {shareGiftImage && <img src={URL.createObjectURL(shareGiftImage)} alt="Free gift preview" style={{ display: "block", width: 120, height: 100, objectFit: "contain", background: "#fff", borderRadius: 10, marginTop: 8, border: "1px solid #dbeafe" }} />}
              </label>
            </div>
          )}
        </div>

        {/* SEPARATE PRODUCT PREVIEW IMAGE */}
        <div style={{ gridColumn: "1 / -1", padding: 16, border: "1px solid #c7d2fe", borderRadius: 12, background: "#f8faff" }}>
          <strong style={{ display: "block", marginBottom: 6 }}>Product Preview Image (separate upload)</strong>
          <small style={{ display: "block", color: "#475569", marginBottom: 10 }}>Recommended: 1200 × 800 px (3:2 landscape), JPG/PNG, ideally under 500 KB. This is the primary thumbnail/preview image.</small>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > 5 * 1024 * 1024) { alert("Preview image must be 5 MB or smaller."); e.target.value = ""; return; }
            setProductPreviewImage((prev) => { if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl); return { file, previewUrl: URL.createObjectURL(file) }; });
            e.target.value = "";
          }} />
          {productPreviewImage?.previewUrl && <div style={{ marginTop: 10 }}><img src={productPreviewImage.previewUrl} alt="Product preview" style={{ width: 180, height: 120, objectFit: "cover", borderRadius: 8, border: "1px solid #cbd5e1" }} /><button type="button" onClick={() => setProductPreviewImage((prev) => { if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl); return null; })} style={{ marginLeft: 10 }}>Remove</button></div>}
        </div>

        {/* PRODUCT LISTING / GALLERY IMAGES */}
        <div style={{ gridColumn: "1 / -1", marginTop: 4, marginBottom: -4 }}>
          <strong style={{ display: "block", fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 5 }}>
            Upload Product Images
          </strong>
          <small style={{ display: "block", color: "#64748b" }}>
            Add the product photos customers will see in the product listing and gallery. You can upload 1–12 images.
          </small>
        </div>
        <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(220px,320px)", gap: 12, alignItems: "stretch" }}>
          <label style={styles.uploadBtn}>
            ➕ Add Image
            <input
              type="file"
              accept="image/*"
              hidden
              multiple
              onChange={handleImageChange}
            />
          </label>
          <label style={{ ...styles.input, margin: 0, display: "flex", alignItems: "center", gap: 10, fontWeight: 800, boxSizing: "border-box" }}>
            <span style={{ whiteSpace: "nowrap" }}>Image orientation</span>
            <select
              value={defaultOrientation}
              onChange={(e) => setDefaultOrientation(e.target.value)}
              style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: "1px solid #d0d7ff", fontWeight: 700, minWidth: 0 }}
            >
              {IMAGE_ORIENTATIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
        <small style={{ gridColumn: "1 / -1", color: "#64748b", marginTop: -6 }}>
          Listing/gallery images: recommended 1200 × 800 px (3:2 landscape), JPG/PNG/WebP, up to 5 MB each. Upload 1–12 images. Select the orientation before adding images; you can change each image orientation below.
        </small>

        {images.length > 0 && (
          <div
            style={styles.previewGrid}
          >
            {images.map(
              (file, index) => (
                <div
                  key={index}
                  style={
                    styles.previewBox
                  }
                >
                  <img
                    src={file.previewUrl}
                    alt="preview"
                    style={
                      styles.previewImage
                    }
                  />

                  <label style={{ display: "block", marginTop: 8, fontSize: 12, fontWeight: 800, color: "#374151" }}>
                    Image orientation
                    <select
                      value={file.orientation}
                      onChange={(e) => setImages((prev) => prev.map((item, i) => i === index ? { ...item, orientation: e.target.value } : item))}
                      style={{ width: "100%", marginTop: 5, padding: "8px 10px", borderRadius: 8, border: "1px solid #d0d7ff", fontWeight: 700 }}
                    >
                      {IMAGE_ORIENTATIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    onClick={() =>
                      removeImage(
                        index
                      )
                    }
                    style={
                      styles.removeBtn
                    }
                  >
                    ✕
                  </button>
                </div>
              )
            )}
          </div>
        )}

        <small
          style={{
            gridColumn:
              "1 / -1",
            color: "#666",
          }}
        >
          Upload minimum{" "}
          {MIN_PRODUCT_IMAGES} and
          maximum{" "}
          {MAX_PRODUCT_IMAGES} product
          images.
        </small>

        {/* SUBMIT */}
        <button
          style={styles.button}
          disabled={loading}
        >
          {loading
            ? "Listing…"
            : "List Product"}
        </button>
      </form>
    </div>
  );
};

export default ListingItem;

/* ================= STYLES ================= */

const styles = {
  card: {
    maxWidth: 900,
    margin: "40px auto",
    padding: 30,
    borderRadius: 16,
    background:
      "linear-gradient(145deg,#ffffff,#f0f4ff)",
    boxShadow:
      "0 20px 40px rgba(0,0,0,0.1)",
  },

  title: {
    marginBottom: 20,
    fontSize: 24,
    fontWeight: 700,
    color: "#2b2f77",
  },

  grid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit,minmax(240px,1fr))",
    gap: 16,
  },

  input: {
    padding: "12px 14px",
    borderRadius: 10,
    border:
      "1px solid #d0d7ff",
    fontSize: 14,
  },

  textarea: {
    gridColumn: "1 / -1",
    minHeight: 100,
    padding: 14,
    borderRadius: 10,
    border:
      "1px solid #d0d7ff",
  },

  bundleBox: {
    gridColumn: "1 / -1",
    padding: 16,
    borderRadius: 12,
    background: "#f5f3ff",
    border: "1px solid #c4b5fd",
    display: "grid",
    gap: 12,
  },

  bundleDetails: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
    gap: 14,
    alignItems: "stretch",
  },

  bundleInfo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 5,
    padding: 12,
    borderRadius: 10,
    background: "#ffffff",
    border: "1px solid #ddd6fe",
    color: "#4c1d95",
    fontSize: 13,
  },

  fieldLabel: {
    display: "block",
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 800,
    color: "#374151",
  },

  optionBox: {
    gridColumn: "1 / -1",
    display: "flex",
    gap: 20,
    flexWrap: "wrap",
    padding: 12,
    borderRadius: 10,
    background: "#f8f9ff",
    border:
      "1px solid #d0d7ff",
  },

  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
  },

  uploadBtn: {
    gridColumn: "1 / -1",
    padding: 12,
    borderRadius: 10,
    border:
      "1px dashed #5f6cff",
    background: "#f4f6ff",
    textAlign: "center",
    cursor: "pointer",
    fontWeight: 600,
  },

  previewGrid: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fill,minmax(120px,1fr))",
    gap: 12,
  },

  previewBox: {
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 12,
    overflow: "hidden",
    border:
      "1px solid #d0d7ff",
  },

  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  removeBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "none",
    background:
      "rgba(0,0,0,0.6)",
    color: "#fff",
    cursor: "pointer",
  },

  button: {
    gridColumn: "1 / -1",
    padding: 14,
    borderRadius: 12,
    border: "none",
    background:
      "linear-gradient(135deg,#5f6cff,#8f9bff)",
    color: "#fff",
    fontWeight: 600,
    fontSize: 16,
    cursor: "pointer",
  },
  variantBox: {
    gridColumn: "1 / -1",
    padding: 18,
    borderRadius: 18,
    background: "#f8fafc",
    border: "1px solid #dbeafe"
  },
  variantHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
    alignItems: "flex-start"
  },
  variantTitle: {
    fontSize: 17,
    fontWeight: 900,
    color: "#0f172a"
  },
  variantHint: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
    maxWidth: 620,
    lineHeight: 1.5
  },
  quickOptions: {
    display: "flex",
    gap: 7,
    flexWrap: "wrap",
    justifyContent: "flex-end"
  },
  quickOptionBtn: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    padding: "8px 10px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer"
  },
  customOptionBtn: {
    border: "none",
    background: "#0f172a",
    color: "#fff",
    padding: "8px 11px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer"
  },
  optionEditor: {
    marginTop: 14,
    padding: 14,
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e2e8f0"
  },
  optionEditorTop: {
    display: "flex",
    gap: 9,
    alignItems: "center"
  },
  removeOptionBtn: {
    border: "none",
    background: "#fee2e2",
    color: "#b91c1c",
    padding: "10px 12px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer"
  },
  valueRow: {
    display: "flex",
    gap: 8,
    marginTop: 9,
    alignItems: "center"
  },
  removeValueBtn: {
    border: "none",
    background: "#f1f5f9",
    padding: "9px 11px",
    borderRadius: 9,
    cursor: "pointer"
  },
  addValueBtn: {
    marginTop: 10,
    border: "none",
    background: "#e0f2fe",
    color: "#0369a1",
    padding: "8px 11px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer"
  },
  generateBtn: {
    marginTop: 14,
    width: "100%",
    border: "none",
    background: "#2563eb",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 12,
    fontWeight: 900,
    cursor: "pointer"
  },
  variantTableWrap: {
    marginTop: 14,
    overflowX: "auto"
  },
  variantTable: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "collapse",
    fontSize: 13
  },
  variantInput: {
    width: 92,
    padding: "7px 8px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    boxSizing: "border-box"
  },
  variantLimit: {
    display: "block",
    marginTop: 10,
    color: "#64748b"
  },
};